import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Hls from 'hls.js';

export const ReactHLSPlayer = forwardRef(
  ({ hlsConfig, src, autoPlay, stopped, startPosition = -1, ...properties }, reference) => {
    const playerReference = reference;
    const hlsReference = useRef();
    const [playerLoaded, setPlayerLoaded] = useState(false);

    const { t } = useTranslation();
    const errorTPath = 'errors.';

    useEffect(() => {
      const hls = hlsReference.current;
      const _initPlayer = () => {
        if (hls != null) {
          try {
            hls.destroy();
          } catch (error) {
            console.error(error);
          }
        }
        if (playerLoaded) {
          setPlayerLoaded(false);
        }
        const newHls = new Hls({
          enableWorker: false,
          autoStartLoad: false,
          ...hlsConfig,
        });

        hlsReference.current = newHls;

        if (playerReference.current != null) {
          newHls.attachMedia(playerReference.current);
        }

        newHls.on(Hls.Events.MEDIA_ATTACHED, () => {
          setPlayerLoaded(true);
          if (src && !stopped) {
            newHls.loadSource(src);
          }
        });

        newHls.on(Hls.Events.MANIFEST_PARSED, () => {
          newHls.startLoad(startPosition ?? -1);
          if (autoPlay && playerReference && playerReference.current && !stopped) {
            playerReference.current
              .play()
              .then(() => {})
              .catch(error => {
                console.warn('Error with autoplay!');
                console.warn(error);
                playerReference.current.dispatchEvent(
                  new ErrorEvent('error', {
                    ...error,
                    message: t(`${errorTPath}genericError`),
                  }),
                );
              });
          }
        });

        newHls.on(Hls.Events.ERROR, (event, data) => {
          console.error('hls-js error:');
          console.error(data);
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            const request = data?.frag?.url;

            if ((request?.includes('vtt') || data?.details === 'subtitleTrackLoadTimeOut') && !data.fatal) {
              return;
            }

            if (!data.fatal && data.details === Hls.ErrorDetails.FRAG_LOAD_ERROR) {
              return;
            }

            playerReference.current.dispatchEvent(
              new ErrorEvent('error', {
                colno: 0,
                lineno: 91,
                filename: 'ReactHLSPlayer.js',
                error: data,
                message: t(`${errorTPath}networkError`),
                ...data,
              }),
            );
          }
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                newHls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                newHls.recoverMediaError();
                break;
              default:
                console.log('Restarting hls.js player due to error');
                _initPlayer();
                break;
            }
          }
        });
      };

      if (Hls.isSupported()) {
        _initPlayer();
      }

      return () => {
        if (hls != null) {
          try {
            hls.destroy();
          } catch (error) {
            console.error(error);
          }
          setPlayerLoaded(false);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stopped, src]);

    if (Hls.isSupported()) {
      return React.createElement('video', {
        ref: playerReference,
        autoPlay,
        loop: false,
        ...properties,
      });
    }
    return React.createElement('video', {
      ref: playerReference,
      src,
      autoPlay,
      loop: false,
      ...properties,
    });
  },
);
